import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, Button } from 'reactstrap';
import './Minesweeper.css';

const BOARD_SIZE = 10;
const MINES_COUNT = 15;

const createBoard = () => {
  const board = [];
  for (let i = 0; i < BOARD_SIZE; i++) {
    board[i] = [];
    for (let j = 0; j < BOARD_SIZE; j++) {
      board[i][j] = {
        isMine: false,
        isRevealed: false,
        isFlagged: false,
        neighborCount: 0,
      };
    }
  }
  return board;
};

const placeMines = (board) => {
  let minesPlaced = 0;
  while (minesPlaced < MINES_COUNT) {
    const row = Math.floor(Math.random() * BOARD_SIZE);
    const col = Math.floor(Math.random() * BOARD_SIZE);
    if (!board[row][col].isMine) {
      board[row][col].isMine = true;
      minesPlaced++;
    }
  }
  return board;
};

const calculateNeighbors = (board) => {
  for (let row = 0; row < BOARD_SIZE; row++) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      if (!board[row][col].isMine) {
        let count = 0;
        for (let i = -1; i <= 1; i++) {
          for (let j = -1; j <= 1; j++) {
            if (
              row + i >= 0 &&
              row + i < BOARD_SIZE &&
              col + j >= 0 &&
              col + j < BOARD_SIZE &&
              board[row + i][col + j].isMine
            ) {
              count++;
            }
          }
        }
        board[row][col].neighborCount = count;
      }
    }
  }
  return board;
};

const revealCell = (board, row, col) => {
  if (
    row < 0 ||
    row >= BOARD_SIZE ||
    col < 0 ||
    col >= BOARD_SIZE ||
    board[row][col].isRevealed ||
    board[row][col].isFlagged
  ) {
    return board;
  }

  board[row][col].isRevealed = true;

  if (board[row][col].neighborCount === 0 && !board[row][col].isMine) {
    for (let i = -1; i <= 1; i++) {
      for (let j = -1; j <= 1; j++) {
        board = revealCell(board, row + i, col + j);
      }
    }
  }

  return board;
};

const checkWin = (board) => {
  for (let row = 0; row < BOARD_SIZE; row++) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      if (!board[row][col].isMine && !board[row][col].isRevealed) {
        return false;
      }
    }
  }
  return true;
};

const Minesweeper = ({ isOpen, toggle }) => {
  const [board, setBoard] = useState(createBoard());
  const [gameStatus, setGameStatus] = useState('playing'); // 'playing', 'won', 'lost'
  const [flagMode, setFlagMode] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  const initializeBoard = useCallback(() => {
    let newBoard = createBoard();
    newBoard = placeMines(newBoard);
    newBoard = calculateNeighbors(newBoard);
    setBoard(newBoard);
    setGameStatus('playing');
    setStartTime(Date.now());
    setElapsedTime(0);
  }, []);

  useEffect(() => {
    if (isOpen) {
      initializeBoard();
    }
  }, [isOpen, initializeBoard]);

  useEffect(() => {
    let interval;
    if (gameStatus === 'playing' && startTime) {
      interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [gameStatus, startTime]);

  const handleCellClick = (row, col) => {
    if (gameStatus !== 'playing') return;

    const newBoard = [...board.map(row => [...row])];

    if (flagMode) {
      newBoard[row][col].isFlagged = !newBoard[row][col].isFlagged;
      setBoard(newBoard);
      return;
    }

    if (newBoard[row][col].isMine) {
      // Reveal all mines
      for (let i = 0; i < BOARD_SIZE; i++) {
        for (let j = 0; j < BOARD_SIZE; j++) {
          if (newBoard[i][j].isMine) {
            newBoard[i][j].isRevealed = true;
          }
        }
      }
      setBoard(newBoard);
      setGameStatus('lost');
      return;
    }

    revealCell(newBoard, row, col);
    setBoard(newBoard);

    if (checkWin(newBoard)) {
      setGameStatus('won');
    }
  };

  const getCellContent = (cell) => {
    if (cell.isFlagged && !cell.isRevealed) {
      return '🚩';
    }
    if (!cell.isRevealed) {
      return '';
    }
    if (cell.isMine) {
      return <img src="/images/machines/small_thumb.png" alt="mine" style={{ width: '16px', height: '16px', objectFit: 'contain' }} />;
    }
    if (cell.neighborCount > 0) {
      return cell.neighborCount;
    }
    return '';
  };

  const getCellClass = (cell) => {
    let classes = 'minesweeper-cell';
    if (cell.isRevealed) {
      classes += ' revealed';
      if (cell.isMine) {
        classes += ' mine';
      }
    } else {
      classes += ' hidden';
    }
    return classes;
  };

  const resetGame = () => {
    initializeBoard();
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>
        <div className="d-flex justify-content-between align-items-center w-100">
          <span>BuscaMinis</span>
          <div className="d-flex align-items-center">
            <span className="mr-3">⏱️ {elapsedTime}s</span>
            <Button
              color={flagMode ? 'warning' : 'secondary'}
              size="sm"
              onClick={() => setFlagMode(!flagMode)}
              className="mr-2"
            >
              {flagMode ? '🚩' : '👆'} {flagMode ? 'Flag Mode' : 'Reveal Mode'}
            </Button>
            <Button color="primary" size="sm" onClick={resetGame}>
              🔄 New Game
            </Button>
          </div>
        </div>
      </ModalHeader>
      <ModalBody>
        <div className="text-center mb-3">
          {gameStatus === 'won' && <h4 className="text-success">🎉 ¡Ganaste! 🎉</h4>}
          {gameStatus === 'lost' && <h4 className="text-danger">💥 ¡Perdiste! 💥</h4>}
          {gameStatus === 'playing' && <h5>Encuentra las {MINES_COUNT} VACWAYminis</h5>}
        </div>

        <div className="d-flex justify-content-center">
          <div className="minesweeper-board">
            {board.map((row, rowIndex) => (
              <div key={rowIndex} className="minesweeper-row">
                {row.map((cell, colIndex) => (
                  <div
                    key={colIndex}
                    className={getCellClass(cell)}
                    onClick={() => handleCellClick(rowIndex, colIndex)}
                  >
                    {getCellContent(cell)}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-3">
          <small className="text-muted">
            Click para revelar • Mantén presionado el botón de modo bandera para marcar minas
          </small>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default Minesweeper;